import type { CSSProperties } from "react";
import type { Persona } from "../primitives";
import { useRowDetailPanelOptional } from "./RowDetailContext";

/**
 * Sprint 12 — a small clickable pill for a foreign-key target: the referenced
 * record's display value in a type-coloured chip. Click opens that record's
 * detail (default: the slide-in panel; override `onOpen` to push a stack entry
 * or navigate a route). Used inline in table cells, the field-list reference
 * editors, and back-reference rows.
 */

const CHIP_PERSONAS: Persona[] = ["blue", "teal", "purple", "amber", "green", "coral", "pink"];

/** Deterministic colour per entity type so the same type always reads alike. */
export function personaForType(typeSlug: string): Persona {
  let hash = 0;
  for (let i = 0; i < typeSlug.length; i++) hash = (hash * 31 + typeSlug.charCodeAt(i)) >>> 0;
  return CHIP_PERSONAS[hash % CHIP_PERSONAS.length]!;
}

const PERSONA_CHIP: Record<Persona, { bg: string; fg: string }> = {
  purple: { bg: "var(--c-purple-50)", fg: "var(--c-purple-800)" },
  teal: { bg: "var(--c-teal-50)", fg: "var(--c-teal-800)" },
  coral: { bg: "var(--c-coral-50)", fg: "var(--c-coral-800)" },
  pink: { bg: "var(--c-pink-50)", fg: "var(--c-pink-800)" },
  gray: { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" },
  blue: { bg: "var(--c-blue-50)", fg: "var(--c-blue-800)" },
  green: { bg: "var(--c-green-50)", fg: "var(--c-green-800)" },
  amber: { bg: "var(--c-amber-50)", fg: "var(--c-amber-800)" },
  red: { bg: "var(--c-red-50)", fg: "var(--c-red-800)" },
};

interface EntityChipProps {
  id: string;
  name: string;
  typeSlug: string;
  /** Override the open behaviour (e.g. push onto the panel stack, or navigate). */
  onOpen?: (entityTypeSlug: string, entityId: string) => void;
  /** Show the `[x]` remove affordance (reference editors). */
  onRemove?: () => void;
}

export function EntityChip({ id, name, typeSlug, onOpen, onRemove }: EntityChipProps) {
  // Provider-optional: on the v3 App-Shell surfaces there is no RowDetailProvider,
  // so `panel` is null and we rely on the caller's `onOpen` (navigation). Where a
  // provider exists (Sprint-12 surfaces) the slide-in panel opens as before.
  const panel = useRowDetailPanelOptional();
  const openTarget = onOpen ?? panel?.openPanel;
  const colors = PERSONA_CHIP[personaForType(typeSlug)];

  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: colors.bg,
    color: colors.fg,
    padding: "1px 8px",
    borderRadius: "var(--kuvio-pill-radius)",
    fontSize: 11,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    lineHeight: 1.5,
    border: "none",
    cursor: "pointer",
    maxWidth: "100%",
  };

  return (
    <span
      role="button"
      tabIndex={0}
      title={`${name} (${typeSlug})`}
      onClick={(e) => {
        e.stopPropagation();
        openTarget?.(typeSlug, id);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.stopPropagation();
          openTarget?.(typeSlug, id);
        }
      }}
      style={style}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name || id}
      </span>
      {onRemove && (
        <i
          className="ti ti-x"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{ fontSize: 11, opacity: 0.6, cursor: "pointer" }}
        />
      )}
    </span>
  );
}
